import { isOffPlatformPayment } from '../types';
import type { OfferPayment } from '../types';

export const MUSICADE_COMMISSION_RATE = 0.0681;
export const MUSICADE_COMMISSION_FIXED = 0.66;
export const PROCESSING_FEE_RATE = 0.0299;
export const PROCESSING_FEE_FIXED = 0.31;

export interface OfferFeeBreakdown {
  gigAmount: number;
  processingFee: number;
  creatorTotal: number;
  musicadeCommission: number;
  candidatePayout: number;
  /** True when the numbers came from a stored payment, not the preview formula. */
  fromPayment: boolean;
}

export function roundMoney(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

export function formatMoney(amount: number, currency: string): string {
  const symbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
  return `${symbol}${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function calculateOfferFees(gigAmount: number): OfferFeeBreakdown {
  const base = Number.isFinite(gigAmount) && gigAmount > 0 ? gigAmount : 0;
  if (base <= 0) {
    return {
      gigAmount: 0,
      processingFee: 0,
      creatorTotal: 0,
      musicadeCommission: 0,
      candidatePayout: 0,
      fromPayment: false,
    };
  }
  const processingFee = roundMoney(base * PROCESSING_FEE_RATE + PROCESSING_FEE_FIXED);
  const musicadeCommission = roundMoney(
    base * MUSICADE_COMMISSION_RATE + MUSICADE_COMMISSION_FIXED,
  );
  return {
    gigAmount: roundMoney(base),
    processingFee,
    creatorTotal: roundMoney(base + processingFee),
    musicadeCommission,
    candidatePayout: roundMoney(Math.max(0, base - musicadeCommission)),
    fromPayment: false,
  };
}

export function feeBreakdownFromPayment(payment: OfferPayment): OfferFeeBreakdown | null {
  const hasStored =
    payment.processingFeeAmount != null ||
    payment.platformFeeAmount != null ||
    payment.payerTotalAmount != null ||
    payment.providerPayoutAmount != null;
  if (!hasStored) return null;

  const gigAmount = payment.amount;
  const processingFee = payment.processingFeeAmount ?? 0;
  const musicadeCommission = payment.platformFeeAmount ?? 0;
  return {
    gigAmount,
    processingFee,
    creatorTotal: payment.payerTotalAmount ?? roundMoney(gigAmount + processingFee),
    musicadeCommission,
    candidatePayout:
      payment.providerPayoutAmount ?? roundMoney(Math.max(0, gigAmount - musicadeCommission)),
    fromPayment: true,
  };
}

export function resolveOfferFees(gigAmount: number, payments?: OfferPayment[]): OfferFeeBreakdown {
  const stripePayments = payments?.filter((p) => !isOffPlatformPayment(p)) ?? [];
  if (stripePayments.length > 0) {
    const newestWithFees = [...stripePayments]
      .reverse()
      .map((payment) => feeBreakdownFromPayment(payment))
      .find((breakdown) => breakdown != null);
    if (newestWithFees) return newestWithFees;
  }
  return calculateOfferFees(gigAmount);
}
